import { useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { Moon, Sun } from 'lucide-react'
import { getThemeSnapshot, subscribeTheme, toggleThemeMode, type ThemeSnapshot } from './theme'

gsap.registerPlugin(useGSAP)

interface ThemeToggleButtonProps {
  className?: string
}

export function ThemeToggleButton({ className = '' }: ThemeToggleButtonProps) {
  const [theme, setTheme] = useState<ThemeSnapshot>(() => getThemeSnapshot())
  const iconRef = useRef<SVGSVGElement>(null)
  const previousTheme = useRef(theme.resolved)

  useEffect(() => subscribeTheme(setTheme), [])

  useGSAP(() => {
    const icon = iconRef.current
    if (!icon || previousTheme.current === theme.resolved) return
    previousTheme.current = theme.resolved
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(icon, { clearProps: 'transform,opacity' })
      return
    }
    gsap.fromTo(icon,
      { rotation: -16, scale: .86, opacity: .48 },
      { rotation: 0, scale: 1, opacity: 1, duration: .18, ease: 'power2.out', clearProps: 'transform,opacity', overwrite: 'auto' },
    )
  }, { dependencies: [theme.resolved], revertOnUpdate: true })

  const nextLabel = theme.resolved === 'dark' ? '切换为浅色模式' : '切换为深色模式'

  return (
    <button
      className={`theme-toggle-button${className ? ` ${className}` : ''}`}
      type="button"
      aria-label={nextLabel}
      title={nextLabel}
      data-theme-state={theme.resolved}
      onClick={() => setTheme(toggleThemeMode())}
    >
      {theme.resolved === 'dark' ? <Sun ref={iconRef} aria-hidden="true" /> : <Moon ref={iconRef} aria-hidden="true" />}
      <span>{theme.resolved === 'dark' ? '浅色模式' : '深色模式'}</span>
    </button>
  )
}
